import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useEffect, useRef, useState } from "react";
import { loginRequest } from "../services/msal/msalConfig";
import Loader from "../../components/Loader/Loader";
import SignInInProgress from "@/features/SignInInProgress/SignInInProgress";
import type { SignInReason } from "@/features/SignInInProgress/types/SignInInProgress.type";
import {
  SIGN_IN_ELSEWHERE_POLL_MS,
  SIGN_IN_REASONS,
} from "@/shared/constants/auth.constants";
import { Outlet } from "react-router-dom";
import commonServices from "../services/common/commonServices";
import {
  onSignOutFromOtherTab,
  requestLoginRedirect,
  resumeLoginAfterUserAction,
} from "../services/auth/authSync";
import {
  SYSTEM_CONFIGURATION_KEYS,
  ChartTypeOptions,
} from "../constants";
import { useAppDispatch } from "../../store/hooks";
import {
  setDecimalValue,
  setChartType,
  setPowerOutageReasonCode,
  setNem12ViewerFilesLimit,
} from "../../store/slices/systemConfigurationSlice";
import { UserInfo } from "../type/type";
import { setPermissionsFromUserInfo } from "@/store/slices/permissionSlice";
import { setTheme, setUserSettings } from "@/store/slices/profileSlice";
import { setTimezone } from "@/store/slices/dashboardFilterSlice";
import {
  applyThemeColor,
  applyThemeClass,
  normalizeThemeType,
  resolveActiveThemeColor,
} from "./theme.helper";
import {
  getSystemProfileSettings,
  resolveActiveTimezone,
} from "./profileSettings.helper";
import { hasPendingLogout, isPasswordResetAccount } from "./authSession.helper";

const ProtectedRoute = (): React.ReactElement => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isLogoutPending = hasPendingLogout();
  const permissionsFetchedRef = useRef(false);
  const dispatch = useAppDispatch();
  const [isSignedOutElsewhere, setIsSignedOutElsewhere] = useState(false);
  const [signInElsewhereReason, setSignInElsewhereReason] = useState<
    SignInReason | null
  >(null);
  const isSignInOpenElsewhere = signInElsewhereReason !== null;

  // Detect reset policy account before rendering
  const activeAccount =
    instance.getActiveAccount() ||
    accounts.find((account) => !isPasswordResetAccount(account)) ||
    accounts[0];

  const isResetPolicy = isPasswordResetAccount(activeAccount);

  useEffect(() => {
    // Wait for MSAL to finish processing the redirect; an early login redirect would
    // replace the pending login attempt and reject the response being handled.
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    // If returning from reset policy → force login
    if (isResetPolicy) {
      void requestLoginRedirect(loginRequest);
      return;
    }

    if (
      (!isAuthenticated || isLogoutPending) &&
      !isSignInOpenElsewhere &&
      !isSignedOutElsewhere
    ) {
      void requestLoginRedirect(loginRequest);
    }
  }, [
    isAuthenticated,
    isLogoutPending,
    inProgress,
    isResetPolicy,
    isSignInOpenElsewhere,
    isSignedOutElsewhere,
  ]);

  useEffect(
    () =>
      onSignOutFromOtherTab(() => {
        setIsSignedOutElsewhere(true);
      }),
    [],
  );

  useEffect(() => {
    if (!isSignedOutElsewhere) {
      return;
    }

    const handleUserAction = (event: Event): void => {
      setIsSignedOutElsewhere(false);
      if (!hasPendingLogout() && instance.getAllAccounts().length > 0) {
        // Already signed in again from another tab; let the action continue.
        // resumeLoginAfterUserAction();
        event.preventDefault();
        event.stopPropagation();
        window.location.reload();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      // The tab that logged out is already on the sign-in page; avoid a second B2C journey.
      setSignInElsewhereReason(SIGN_IN_REASONS.signedOut);
    };

    document.addEventListener("click", handleUserAction, true);
    document.addEventListener("keydown", handleUserAction, true);
    return (): void => {
      document.removeEventListener("click", handleUserAction, true);
      document.removeEventListener("keydown", handleUserAction, true);
    };
  }, [isSignedOutElsewhere, instance]);

  useEffect(() => {
    if (!isSignInOpenElsewhere) {
      return;
    }

    const reloadIfSignedIn = (): void => {
      if (!hasPendingLogout() && instance.getAllAccounts().length > 0) {
        window.location.reload();
      }
    };

    const intervalId = window.setInterval(
      reloadIfSignedIn,
      SIGN_IN_ELSEWHERE_POLL_MS,
    );
    document.addEventListener("visibilitychange", reloadIfSignedIn);
    return (): void => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", reloadIfSignedIn);
    };
  }, [isSignInOpenElsewhere, instance]);

  const signInHere = (): void => {
    resumeLoginAfterUserAction();
    void requestLoginRedirect(loginRequest);
  };

  useEffect(() => {
    // Fetch permissions once after authentication is confirmed
    if (
      !isAuthenticated ||
      isLogoutPending ||
      inProgress !== InteractionStatus.None ||
      isResetPolicy ||
      permissionsFetchedRef.current
    ) {
      return;
    }

    // Avoid duplicate permission bootstrap work during StrictMode re-renders.
    permissionsFetchedRef.current = true;

    const fetchPermissions = async (): Promise<void> => {
      try {
        if (activeAccount) {
          const tokenClaims = activeAccount.idTokenClaims as Record<
            string,
            unknown
          >;

          // Runtime type check + safe parse with fallback
          if (
            tokenClaims.userData &&
            typeof tokenClaims.userData === "string"
          ) {
            try {
              const userInfo = JSON.parse(tokenClaims.userData) as UserInfo;

              dispatch(setPermissionsFromUserInfo(userInfo));
            } catch {
              // Invalid JSON → dispatch empty to transition to loaded state
              dispatch(setPermissionsFromUserInfo({} as UserInfo));
            }
          } else {
            // userData missing or not a string → dispatch empty to transition to loaded state
            dispatch(setPermissionsFromUserInfo({} as UserInfo));
          }
        } else {
          // No active account → dispatch empty to transition to loaded state
          dispatch(setPermissionsFromUserInfo({} as UserInfo));
        }

        const [systemConfigResponse, userSettingsResponse] = await Promise.all([
          commonServices.getSystemConfiguration(),
          commonServices.getUserSettings(),
        ]);

        const systemProfileSettings = getSystemProfileSettings(
          systemConfigResponse?.data,
        );
        const systemThemeColor = systemProfileSettings.themeColor;

        if (
          userSettingsResponse.success &&
          userSettingsResponse.data?.settings
        ) {
          const settings = userSettingsResponse.data.settings;
          const normalizedTheme = normalizeThemeType(settings.themeType);

          dispatch(setUserSettings(settings));
          dispatch(setTheme(normalizedTheme));
          applyThemeClass(normalizedTheme);
          applyThemeColor(
            resolveActiveThemeColor(settings.themeColor, systemThemeColor),
          );

          if (settings.defaultChartType) {
            const chartType =
              settings.defaultChartType.toLowerCase() ===
              ChartTypeOptions[1].label.toLowerCase()
                ? ChartTypeOptions[1].value
                : ChartTypeOptions[0].value;
            dispatch(setChartType(chartType as "bar" | "line"));
          }
        }

        if (systemConfigResponse?.data) {
          const decimalConfig = systemConfigResponse.data.find(
            (config) =>
              config.configuration_name ===
              SYSTEM_CONFIGURATION_KEYS.decimalValue,
          );
          const powerOutageReasonCodeConfig = systemConfigResponse.data.find(
            (config) =>
              config.configuration_name ===
              SYSTEM_CONFIGURATION_KEYS.powerOutageReasonCode,
          );
          const nem12ViewerFilesLimitConfig = systemConfigResponse.data.find(
            (config) =>
              config.configuration_name ===
              SYSTEM_CONFIGURATION_KEYS.nem12ViewerFilesLimit,
          );
          if (powerOutageReasonCodeConfig) {
            dispatch(
              setPowerOutageReasonCode(
                powerOutageReasonCodeConfig.configuration_value,
              ),
            );
          }

          if (nem12ViewerFilesLimitConfig) {
            dispatch(
              setNem12ViewerFilesLimit(
                Number(nem12ViewerFilesLimitConfig.configuration_value),
              ),
            );
          }

          if (decimalConfig) {
            dispatch(
              setDecimalValue(Number(decimalConfig.configuration_value)),
            );
          }

          if (
            (!userSettingsResponse.success ||
              !userSettingsResponse.data?.settings.themeColor) &&
            systemThemeColor
          ) {
            applyThemeColor(
              resolveActiveThemeColor(
                userSettingsResponse.data?.settings.themeColor,
                systemThemeColor,
              ),
            );
          }

          if (
            systemProfileSettings.defaultChartType &&
            (!userSettingsResponse.success ||
              !userSettingsResponse.data?.settings.defaultChartType)
          ) {
            const chartType =
              systemProfileSettings.defaultChartType.toLowerCase() ===
              ChartTypeOptions[1].value
                ? ChartTypeOptions[1].value
                : ChartTypeOptions[0].value;
            dispatch(setChartType(chartType as "bar" | "line"));
          }
        }

        dispatch(
          setTimezone(
            resolveActiveTimezone(
              userSettingsResponse.data?.settings.defaultTimezone,
              systemProfileSettings.defaultTimezone,
            ),
          ),
        );
      } catch {
        // Ensure permissions are marked loaded even on catastrophic failure
        dispatch(setPermissionsFromUserInfo({} as UserInfo));
      }
    };

    void fetchPermissions();
  }, [
    inProgress,
    isAuthenticated,
    isLogoutPending,
    isResetPolicy,
    activeAccount,
    dispatch,
    instance,
  ]);

  if (signInElsewhereReason !== null) {
    return (
      <SignInInProgress
        reason={signInElsewhereReason}
        onSignInHere={signInHere}
      />
    );
  }

  // Signed out in another tab: keep the current page until the user interacts.
  if (isSignedOutElsewhere) {
    return <Outlet />;
  }

  if (
    inProgress !== InteractionStatus.None ||
    !isAuthenticated ||
    isLogoutPending ||
    isResetPolicy
  ) {
    return <Loader />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
